"use client";

import { createContext, type ReactNode, useContext, useState } from "react";

interface TabItem {
	id: string;
	name: string;
	nameEn: string;
	href: string;
	icon: React.ReactNode;
}

interface TabGroup {
	id: string;
	name: string;
	nameEn: string;
	tabs: TabItem[];
	activePatterns: string[];
}

interface TabNavigationContextType {
	activeTabGroup: string | null;
	activeTab: string | null;
	setActiveTabGroup: (groupId: string | null) => void;
	setActiveTab: (tabId: string) => void;
	getTabGroups: () => TabGroup[];
	getCurrentTabGroup: () => TabGroup | null;
	getCurrentTab: () => TabItem | null;
	navigateToNextGroup: () => void;
	navigateToPrevGroup: () => void;
}

const TabNavigationContext = createContext<
	TabNavigationContextType | undefined
>(undefined);

export function useTabNavigation() {
	const context = useContext(TabNavigationContext);
	if (context === undefined) {
		throw new Error(
			"useTabNavigation must be used within a TabNavigationProvider",
		);
	}
	return context;
}

interface TabNavigationProviderProps {
	children: ReactNode;
}

export function TabNavigationProvider({
	children,
}: TabNavigationProviderProps) {
	const [activeTabGroup, setActiveTabGroup] = useState<string | null>(null);
	const [activeTab, setActiveTab] = useState<string | null>(null);

	// 定义标签组数据
	const tabGroups: TabGroup[] = [
		{
			id: "data",
			name: "数据",
			nameEn: "Data",
			activePatterns: ["/data", "/scan"],
			tabs: [
				{
					id: "data-overview",
					name: "数据概览",
					nameEn: "Data Overview",
					href: "/data",
					icon: null,
				},
				{
					id: "transaction-monitoring",
					name: "交易监控",
					nameEn: "Transaction Monitoring",
					href: "/scan",
					icon: null,
				},
			],
		},
		{
			id: "activities",
			name: "活动",
			nameEn: "Activities",
			activePatterns: [
				"/plaza/lucky-gift",
				"/plaza/lucky-wheel",
				"/activities",
			],
			tabs: [
				{
					id: "lucky-gift",
					name: "奥丁红包",
					nameEn: "Lucky Gift",
					href: "/plaza/lucky-gift",
					icon: null,
				},
				{
					id: "lucky-wheel",
					name: "奥丁宝符炉",
					nameEn: "Lucky Wheel",
					href: "/plaza/lucky-wheel",
					icon: null,
				},
				{
					id: "airdrop",
					name: "空投",
					nameEn: "Airdrop",
					href: "/activities",
					icon: null,
				},
			],
		},
		{
			id: "tools",
			name: "工具",
			nameEn: "Tools",
			activePatterns: ["/tools"],
			tabs: [
				{
					id: "frontrunner",
					name: "抢跑",
					nameEn: "Frontrunner",
					href: "/tools/frontrunner",
					icon: null,
				},
				{
					id: "batch-airdrop",
					name: "空投",
					nameEn: "Airdrop",
					href: "/tools/batch-airdrop",
					icon: null,
				},
				{
					id: "single-transfer",
					name: "转账",
					nameEn: "Transfer",
					href: "/tools/single-transfer",
					icon: null,
				},
				{
					id: "holdings-monitor",
					name: "持仓",
					nameEn: "Holdings",
					href: "/tools/holdings-monitor",
					icon: null,
				},
				{
					id: "rune-snapshot",
					name: "快照",
					nameEn: "Snapshot",
					href: "/tools/rune-snapshot",
					icon: null,
				},
				{
					id: "rune-bridge",
					name: "跨链",
					nameEn: "Bridge",
					href: "/tools/rune-bridge",
					icon: null,
				},
				{
					id: "rune-address",
					name: "关联",
					nameEn: "Links",
					href: "/tools/rune-address",
					icon: null,
				},
			],
		},
	];

	const getTabGroups = () => tabGroups;

	const getCurrentTabGroup = () => {
		if (!activeTabGroup) return null;
		return tabGroups.find((group) => group.id === activeTabGroup) || null;
	};

	const getCurrentTab = () => {
		const currentGroup = getCurrentTabGroup();
		if (!currentGroup || !activeTab) return null;
		return currentGroup.tabs.find((tab) => tab.id === activeTab) || null;
	};

	const navigateToNextGroup = () => {
		const currentIndex = tabGroups.findIndex(
			(group) => group.id === activeTabGroup,
		);
		if (currentIndex === -1) return;

		const nextIndex = (currentIndex + 1) % tabGroups.length;
		const nextGroup = tabGroups[nextIndex];

		// 跳过广场，因为它不是标签组
		if (nextGroup.id === "plaza") {
			const afterNextIndex = (nextIndex + 1) % tabGroups.length;
			const afterNextGroup = tabGroups[afterNextIndex];
			setActiveTabGroup(afterNextGroup.id);
			setActiveTab(afterNextGroup.tabs[0].id);
		} else {
			setActiveTabGroup(nextGroup.id);
			setActiveTab(nextGroup.tabs[0].id);
		}
	};

	const navigateToPrevGroup = () => {
		const currentIndex = tabGroups.findIndex(
			(group) => group.id === activeTabGroup,
		);
		if (currentIndex === -1) return;

		const prevIndex =
			currentIndex === 0 ? tabGroups.length - 1 : currentIndex - 1;
		const prevGroup = tabGroups[prevIndex];

		// 如果是广场，继续向前找
		if (prevGroup.id === "plaza") {
			const beforePrevIndex =
				prevIndex === 0 ? tabGroups.length - 1 : prevIndex - 1;
			const beforePrevGroup = tabGroups[beforePrevIndex];
			setActiveTabGroup(beforePrevGroup.id);
			setActiveTab(beforePrevGroup.tabs[beforePrevGroup.tabs.length - 1].id);
		} else {
			setActiveTabGroup(prevGroup.id);
			setActiveTab(prevGroup.tabs[prevGroup.tabs.length - 1].id);
		}
	};

	return (
		<TabNavigationContext.Provider
			value={{
				activeTabGroup,
				activeTab,
				setActiveTabGroup,
				setActiveTab,
				getTabGroups,
				getCurrentTabGroup,
				getCurrentTab,
				navigateToNextGroup,
				navigateToPrevGroup,
			}}
		>
			{children}
		</TabNavigationContext.Provider>
	);
}
